import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { countBy, isEmpty, mapValues, noop } from 'lodash-es';
import { PusherEventName } from 'anchor-scout-common/types/pusher';
import { Page, Button, Switch, TableList, Typography, useStateInRef, PageEmptyState, PageErrorState, PageContentHeader, ConfirmationModal, useOverlayContext, FilterPickerGroup, Row, useDevice } from 'seagull';
import { Settings } from 'seagull/icons';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import ConfirmExternallySignModal from '~/components/ConfirmExternallySignModal/ConfirmExternallySignModal';
import ExternalLinkButton from '~/components/ExternalLinkButton';
import TableFilters from '~/components/TableFilters/TableFilters';
import { useStore, useStoreActions } from '~/contexts';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { useCreateRelationshipFromDraft } from '~/hooks/useCreateRelationshipFromDraft';
import { useDebouncedLogSearchToBI } from '~/hooks/useDebouncedLogSearchToBI';
import { useInfiniteScroll } from '~/hooks/useInfiniteScroll';
import { useSetDocumentTitle } from '~/hooks/useSetDocumentTitle';
import { useBulkActionModal } from '~/hooks/useTerminateAgreement/useBulkActionModal';
import { ErrorResponse } from '~/infra/ErrorResponse';
import { handlePromise } from '~/infra/utils';
import { BusinessChannelEventBinder } from '~/pusher/BusinessChannelEventBinder';
import { selectors } from '~/store/state-selectors';
import pluralize from '~/utils/pluralize';
import { CreateProposalButton } from '../../components/CreateProposalButton/CreateProposalButton';
import PageLoadingState from '../../components/PageLoadingState';
import { VisibleColumnsPicker } from '../../components/VisibleColumnsPicker';
import BulkActions from './BulkActions';
import styles from './DraftsDashboard.module.scss';
import { bulkExternallySignErrorText, filterAndSortDrafts } from './DraftsDashboard.utils';
import DraftsDashboardRow from './DraftsDashboardRow';
import EditDraftsTable from './EditDraftsTable/EditDraftsTable';
import { EditDraftsTableColumns } from './EditDraftsTable/types';
import { EmptyStateIllustration } from './EmptyStateIllustration';
const title = 'Drafts';
const tableDefs = [
    {
        headerItem: {
            id: 'Company name',
            value: 'Company name'
        },
        columnDef: {
            width: [
                180,
                400
            ],
            sortable: true,
            mobile: {}
        },
        defaultSortDirection: 'desc'
    },
    {
        headerItem: {
            id: 'Modified by',
            value: 'Modified by'
        },
        columnDef: {
            width: [
                120,
                180
            ],
            sortable: true,
            mobile: {
                label: {
                    text: 'Modified by'
                }
            }
        },
        defaultSortDirection: 'desc'
    },
    {
        headerItem: {
            id: 'Last modified',
            value: 'Last modified'
        },
        columnDef: {
            width: 110,
            sortable: true,
            mobile: {
                label: {
                    text: 'Last modified'
                }
            }
        },
        defaultSortDirection: 'desc'
    }
];
export const headerIds = tableDefs.map((d)=>d.headerItem.id);
const headerItems = tableDefs.map((d)=>d.headerItem);
const columnDefs = tableDefs.map((d)=>d.columnDef);
const sortOptions = tableDefs.flatMap((d, colIndex)=>d.columnDef.sortable ? [
        {
            id: d.headerItem.id,
            label: d.headerItem.value,
            defaultDirection: d.defaultSortDirection,
            colIndex
        }
    ] : []);
function ActualDraftsDashboard(props) {
    const { data, isRefreshing, refresh } = props;
    const [sort, setSort] = useState({
        colIndex: 2,
        sort: 'desc'
    });
    const { api, logBI } = useStoreActions();
    const { isMobileDevice } = useDevice();
    const overlayContext = useOverlayContext();
    const createRelationshipFromDraft = useCreateRelationshipFromDraft();
    const isVendor = useStore(selectors.user.isVendor);
    const scrollElRef = useRef(null);
    const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11352);
    const [selected, setSelected] = useState({});
    const getSelected = useStateInRef(selected);
    const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
    const numSelected = useMemo(()=>{
        var _countBy_true;
        return (_countBy_true = countBy(selected).true) !== null && _countBy_true !== void 0 ? _countBy_true : 0;
    }, [
        selected
    ]);
    const { doBulkAction } = useBulkActionModal();
    const hasDrafts = data && data.length > 0;
    const [advancedEditMode, setAdvancedEditMode] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState({
        [EditDraftsTableColumns.PrimaryContact]: true,
        [EditDraftsTableColumns.AdditionalRecipients]: true,
        [EditDraftsTableColumns.AgreementName]: true,
        [EditDraftsTableColumns.EffectiveDate]: true,
        [EditDraftsTableColumns.ModifiedBy]: true,
        [EditDraftsTableColumns.LastModified]: true,
        [EditDraftsTableColumns.ExpirationDate]: true,
        [EditDraftsTableColumns.IntroductoryMessage]: true,
        [EditDraftsTableColumns.IntroductoryVideo]: true,
        [EditDraftsTableColumns.TermsOfService]: true,
        [EditDraftsTableColumns.AgreementNote]: true,
        [EditDraftsTableColumns.AutoApproveAmendments]: true,
        [EditDraftsTableColumns.AutoApproveOutOfScopeCharges]: true,
        [EditDraftsTableColumns.NumberOfDaysUntilPaymentIsDue]: true,
        [EditDraftsTableColumns.PaymentMethodRequiredForSigning]: true,
        [EditDraftsTableColumns.CreditCardProcessingFees]: true,
        [EditDraftsTableColumns.ManualApprovalOfEachPayment]: true
    });
    const { urlState: { search }, changeUrlState } = useStateFromUrl({
        search: {
            defaultValue: ''
        }
    });
    const { drafts, draftsMap } = useMemo(()=>filterAndSortDrafts(data, search, sort), [
        data,
        sort,
        search
    ]);
    const { paginatedData, onScroll } = useInfiniteScroll({
        data: drafts || [],
        scrollElRef
    });
    const editDraftsTableRef = useRef(null);
    const onToggleAllSelection = useCallback((selectAll)=>{
        setSelected(drafts.reduce((acc, param)=>{
            let { id } = param;
            return Object.assign(acc, {
                [id]: selectAll
            });
        }, {}));
    }, [
        drafts
    ]);
    useEffect(()=>{
        // this unselects any proposal template that is not found in the newly fetched list of proposal templates
        if (!drafts || isEmpty(getSelected())) return;
        const ids = drafts.map((proposalTemplate)=>proposalTemplate.id);
        const newSelected = mapValues(getSelected(), (origValue, proposalTemplateId)=>ids.includes(proposalTemplateId) ? origValue : false);
        setSelected(newSelected);
    }, [
        drafts
    ]);
    function getSelectedDrafts() {
        return Object.entries(selected).filter((param)=>{
            let [, isSelected] = param;
            return isSelected;
        }).map((param)=>{
            let [draftId, _] = param;
            return draftId;
        });
    }
    async function bulkSend() {
        var _editDraftsTableRef_current;
        const selectedDrafts = getSelectedDrafts();
        await doBulkAction({
            bulkItemIds: selectedDrafts,
            confirmation: {
                title: 'Send proposals in bulk',
                primaryButtonLabel: 'Send proposals',
                content: /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            children: [
                                'By selecting "Send Proposals," a proposal will be sent to each client of the',
                                ' ',
                                /*#__PURE__*/ _jsx(Typography, {
                                    testId: "drafts-dashboard-remove-modal-template-name",
                                    as: "span",
                                    weight: "bold",
                                    children: selectedDrafts.length
                                }),
                                ' ',
                                "selected drafts?"
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            children: "Consider sending a personal message to clients still transitioning to Anchor."
                        })
                    ]
                })
            },
            progressTitle: 'Send proposals in bulk',
            actionName: 'sent',
            itemName: 'proposal',
            errorExtra: 'due to missing or incorrect settings',
            canProcessItem: ()=>true,
            processSingleItem: (draftId)=>handlePromise(createRelationshipFromDraft(draftId).catch((err)=>{
                    var _draft_contactDetails;
                    const draft = drafts.find((draft)=>draft.id === draftId);
                    const email = draft === null || draft === void 0 ? void 0 : (_draft_contactDetails = draft.contactDetails) === null || _draft_contactDetails === void 0 ? void 0 : _draft_contactDetails.email;
                    const errorText = new ErrorResponse(err).createRelationshipErrorText();
                    const draftInfo = "".concat(draft ? draft.title : 'Draft').concat(email ? " (".concat(email, ")") : '');
                    throw "".concat(draftInfo, " - ").concat(errorText.text);
                })),
            onDone: (failedDraftIds)=>{
                setSelected(failedDraftIds.reduce((acc, draftId)=>_object_spread_props(_object_spread({}, acc), {
                        [draftId]: true
                    }), {}));
            }
        });
        refresh();
        advancedEditMode && ((_editDraftsTableRef_current = editDraftsTableRef.current) === null || _editDraftsTableRef_current === void 0 ? void 0 : _editDraftsTableRef_current.refresh());
    }
    async function bulkExternallySign() {
        var _editDraftsTableRef_current;
        const selectedDrafts = getSelectedDrafts();
        await doBulkAction({
            bulkItemIds: selectedDrafts,
            confirmation: {
                renderModal: (p)=>/*#__PURE__*/ _jsx(ConfirmExternallySignModal, _object_spread_props(_object_spread({}, p), {
                        bulkData: {
                            numClients: selectedDrafts.length
                        }
                    }))
            },
            progressTitle: 'Approve on behalf of clients in bulk',
            actionName: 'approved',
            progressAction: 'Approving',
            itemName: 'agreement',
            errorExtra: 'because of missing or incorrect configurations',
            canProcessItem: ()=>true,
            processSingleItem: (draftId)=>handlePromise(api.relationship.createFromDraft(draftId, {
                    externallySign: true
                })),
            getErrorText: (draftId, error)=>bulkExternallySignErrorText(draftsMap.get(draftId), error),
            onDone: (failedDraftIds)=>{
                setSelected(failedDraftIds.reduce((acc, draftId)=>_object_spread_props(_object_spread({}, acc), {
                        [draftId]: true
                    }), {}));
            }
        });
        refresh();
        advancedEditMode && ((_editDraftsTableRef_current = editDraftsTableRef.current) === null || _editDraftsTableRef_current === void 0 ? void 0 : _editDraftsTableRef_current.refresh());
    }
    async function bulkDiscard() {
        var _editDraftsTableRef_current;
        const selectedDrafts = getSelectedDrafts();
        await doBulkAction({
            bulkItemIds: selectedDrafts,
            progressAction: 'Discarding',
            confirmation: {
                title: 'Discard drafts',
                primaryButtonLabel: 'Discard',
                content: /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            children: [
                                "Are you sure you want to discard",
                                ' ',
                                /*#__PURE__*/ _jsx(Typography, {
                                    testId: "drafts-dashboard-remove-modal-template-name",
                                    as: "span",
                                    weight: "bold",
                                    children: selectedDrafts.length
                                }),
                                ' ',
                                pluralize('draft', selectedDrafts.length),
                                "?"
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            children: "This action cannot be undone."
                        })
                    ]
                })
            },
            progressTitle: 'Discard drafts',
            actionName: 'discarded',
            itemName: 'draft',
            canProcessItem: ()=>true,
            processSingleItem: (draftId)=>handlePromise(api.draft.deleteById(draftId)),
            onDone: (failedDraftIds)=>{
                setSelected(failedDraftIds.reduce((acc, draftId)=>_object_spread_props(_object_spread({}, acc), {
                        [draftId]: true
                    }), {}));
            }
        });
        refresh();
        advancedEditMode && ((_editDraftsTableRef_current = editDraftsTableRef.current) === null || _editDraftsTableRef_current === void 0 ? void 0 : _editDraftsTableRef_current.refresh());
    }
    const onSortChange = (sort)=>{
        logBI(11350, {
            column: sort ? headerIds[sort.colIndex] : 'none'
        });
        setSort(sort);
    };
    const onClearSearch = ()=>{
        changeUrlState({
            search: ''
        });
        !advancedEditMode && debouncedLogSearchToBI.onClear();
    };
    const openImportDraftsModal = ()=>{
        overlayContext.showModal({
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, {
                    textAlignCenter: false,
                    primaryButtonLabel: "Close",
                    onPrimaryButtonClick: dismissModal,
                    onCloseButtonClick: dismissModal,
                    title: "Import agreements",
                    primaryButtonTestId: "close-import-agreements-modal",
                    content: /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Typography, {
                                children: "Need to import agreements into Anchor that were signed elsewhere?"
                            }),
                            /*#__PURE__*/ _jsxs(Typography, {
                                children: [
                                    /*#__PURE__*/ _jsx(ExternalLinkButton, {
                                        openInNewTab: true,
                                        href: "https://forms.gle/Q9sMHXFcp5Fs866FA",
                                        children: "Complete this form"
                                    }),
                                    ' ',
                                    "and our team will reach out with the next steps."
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                style: {
                                    marginTop: '16px'
                                },
                                children: "Please note: This service is currently available for requests of 30 agreements or more."
                            })
                        ]
                    })
                });
            }
        });
    };
    const updateVisibleColumns = (columns)=>{
        setVisibleColumns(columns);
    };
    const renderEmptyState = ()=>/*#__PURE__*/ _jsx(PageEmptyState, {
            maxHeight: true,
            testId: "empty-state-page",
            illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
            title: "No drafts yet",
            message: "Proposals awaiting completion will appear here.",
            actions: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    !isMobileDevice && /*#__PURE__*/ _jsx(Button, {
                        skin: "primaryOutline",
                        testId: "import-proposals-btn",
                        onClick: ()=>openImportDraftsModal(),
                        children: "Import"
                    }),
                    /*#__PURE__*/ _jsx(CreateProposalButton, {
                        testId: "drafts-empty-state-new-btn",
                        hidePrefix: true
                    })
                ]
            })
        });
    const renderDraftsTable = ()=>/*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(PageContentHeader, {
                    children: /*#__PURE__*/ _jsxs(FilterPickerGroup, {
                        className: styles.filterPickerGroup,
                        children: [
                            /*#__PURE__*/ _jsx(TableFilters, {
                                testIdPrefix: "drafts-dashboard",
                                filterLabels: {},
                                filterConfig: {},
                                selectedFilters: {},
                                setSelectedFilters: noop,
                                onRefresh: (_filters, newSort)=>{
                                    if (newSort) setSort(newSort);
                                },
                                search: {
                                    value: search,
                                    testId: 'drafts-dashboard-search',
                                    registerHotkey: true,
                                    onChange: (event)=>{
                                        const searchStr = event.target.value;
                                        changeUrlState({
                                            search: searchStr
                                        });
                                        !advancedEditMode && debouncedLogSearchToBI.onChange(searchStr);
                                    },
                                    onClear: onClearSearch
                                },
                                mobile: {
                                    sortOptions
                                }
                            }),
                            !isMobileDevice && /*#__PURE__*/ _jsxs(Row, {
                                gap: 16,
                                alignItems: "center",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: styles.advancedEditMode,
                                        children: [
                                            /*#__PURE__*/ _jsx(Typography, {
                                                children: "Advanced edit mode"
                                            }),
                                            /*#__PURE__*/ _jsx(Switch, {
                                                testId: "advanced-edit-mode-switch",
                                                checked: advancedEditMode,
                                                onChange: ()=>setAdvancedEditMode((advancedEditMode)=>!advancedEditMode)
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(BulkActions, {
                                        numSelected: numSelected,
                                        bulkActionsOpen: bulkActionsOpen,
                                        setBulkActionsOpen: setBulkActionsOpen,
                                        bulkSend: bulkSend,
                                        bulkExternallySign: bulkExternallySign,
                                        bulkDiscard: bulkDiscard
                                    }),
                                    advancedEditMode && /*#__PURE__*/ _jsx(VisibleColumnsPicker, {
                                        fixedColumns: {
                                            [EditDraftsTableColumns.PrimaryContact]: true
                                        },
                                        visibleColumns: visibleColumns,
                                        onApply: updateVisibleColumns,
                                        testId: "edit-columns",
                                        title: "Edit columns",
                                        type: "button",
                                        prefixIcon: /*#__PURE__*/ _jsx(Settings, {})
                                    })
                                ]
                            })
                        ]
                    })
                }),
                advancedEditMode ? /*#__PURE__*/ _jsx(EditDraftsTable, {
                    visibleColumns: visibleColumns,
                    ref: editDraftsTableRef,
                    search: search,
                    selected: selected,
                    setSelected: (selected)=>setSelected(selected)
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx(PageContentHeader, {
                            children: /*#__PURE__*/ _jsx(TableList.Header, {
                                items: headerItems,
                                testId: "drafts-dashboard-table-header"
                            })
                        }),
                        drafts.length ? paginatedData.map((d, i)=>/*#__PURE__*/ _jsx(DraftsDashboardRow, {
                                draft: d,
                                index: i,
                                refresh: refresh
                            }, d.id)) : /*#__PURE__*/ _jsx(PageEmptyState, {
                            illustration: /*#__PURE__*/ _jsx(EmptyStateIllustration, {}),
                            title: "No drafts match your request",
                            message: "Try adjusting your search",
                            actions: /*#__PURE__*/ _jsx(Button, {
                                testId: "drafts-dashboard-empty-search-clear-btn",
                                onClick: onClearSearch,
                                children: "Clear search"
                            })
                        })
                    ]
                })
            ]
        });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(BusinessChannelEventBinder, {
                source: "scout",
                eventName: PusherEventName.RelationshipCreatedFromScheduledDraft,
                callback: refresh
            }),
            /*#__PURE__*/ _jsx(TableList.Provider, {
                hasActionColumn: true,
                enableRowSelection: true,
                selectedRows: selected,
                columnDefs: columnDefs,
                sort: sort,
                onSortChange: onSortChange,
                onSelectionChange: setSelected,
                numRows: drafts.length,
                numSelectedRows: numSelected,
                onToggleAllSelection: onToggleAllSelection,
                children: /*#__PURE__*/ _jsx(Page, {
                    testId: "drafts-dashboard",
                    variant: advancedEditMode || !hasDrafts ? 'containedHeight' : 'default',
                    ref: scrollElRef,
                    header: title,
                    actions: hasDrafts && isVendor && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                skin: "primaryOutline",
                                testId: "import-drafts-btn",
                                onClick: ()=>openImportDraftsModal(),
                                children: "Import"
                            }),
                            /*#__PURE__*/ _jsx(CreateProposalButton, {})
                        ]
                    }),
                    loading: isRefreshing,
                    onScroll: onScroll,
                    children: hasDrafts ? renderDraftsTable() : renderEmptyState()
                })
            })
        ]
    });
}
export function DraftsDashboard() {
    const { data: draftsData, error: draftsError, loading: draftsLoading } = useStore((state)=>state.drafts);
    const { contacts, error: contactsError, isLoading: contactsLoading, refresh: fetchContacts } = useContacts({
        refreshOnMount: true
    });
    const { fetchDraftsSummaries } = useStoreActions();
    const fetchData = ()=>{
        return Promise.all([
            fetchContacts(),
            fetchDraftsSummaries()
        ]);
    };
    const draftsWithContactData = useMemo(()=>{
        const contactMap = Object.fromEntries((contacts !== null && contacts !== void 0 ? contacts : []).map((contact)=>[
                contact.id(),
                {
                    firstName: contact.firstName(),
                    lastName: contact.lastName(),
                    email: contact.email(),
                    companyName: contact.companyName()
                }
            ]));
        return (draftsData !== null && draftsData !== void 0 ? draftsData : []).map((draft)=>{
            var _draft_clientContactId;
            return _object_spread_props(_object_spread({}, draft), {
                contactDetails: contactMap[(_draft_clientContactId = draft.clientContactId) !== null && _draft_clientContactId !== void 0 ? _draft_clientContactId : '']
            });
        });
    }, [
        draftsData,
        contacts
    ]);
    useSetDocumentTitle(title);
    useEffect(()=>{
        fetchDraftsSummaries();
    }, []);
    const hasData = draftsData && contacts;
    const isLoading = draftsLoading || contactsLoading;
    const error = draftsError || contactsError;
    if (error) {
        return /*#__PURE__*/ _jsx(PageErrorState, {
            testId: "error-state-page",
            title: title,
            onRefresh: fetchData
        });
    } else if (!hasData && isLoading) {
        return /*#__PURE__*/ _jsx(PageLoadingState, {
            header: title,
            loading: isLoading
        });
    } else {
        return /*#__PURE__*/ _jsx(ActualDraftsDashboard, {
            data: draftsWithContactData,
            isRefreshing: isLoading,
            refresh: fetchData
        });
    }
}
